import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useVarianceAppContext } from './VarianceAppContext';
import axios from 'axios';
import { 
  Settings, 
  Calendar, 
  ChevronDown, 
  X, 
  Play, 
  ChevronRight,
  Filter,
  RefreshCw,
  Table
} from 'lucide-react';
import './LineItemAnalysis.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ModernLineItemAnalysis = ({
  selectedBank = '',
  selectedCategory = '',
  selectedViewType = 'actual',
  selectedPeriodType = '',
  selectedCurrentPeriod = '',
  selectedPreviousPeriod = '',
  selectedColumns = ['line_item', 'current', 'previous', 'variance_amount', 'variance_percent', 'segment', 'ai_explanation'],
  financialItems = []
}) => {
  const { country } = useCountry();
  const { selectedBusinessSegment } = useVarianceAppContext();
  
  // State for data
  const [lineItemData, setLineItemData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for table
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showAll, setShowAll] = useState(false);
  
  // State for columns
  const [availableColumns, setAvailableColumns] = useState([]);
  
  // Available columns configuration
  const columnConfig = {
    line_item: { label: 'LINE ITEM', color: 'purple' },
    current: { label: 'CURRENT', color: 'green' },
    previous: { label: 'PREVIOUS', color: 'blue' },
    variance_amount: { label: 'VARIANCE ($)', color: 'orange' },
    variance_percent: { label: 'VARIANCE (%)', color: 'orange' },
    segment: { label: 'SEGMENT', color: 'blue' },
    ai_explanation: { label: 'AI EXPLANATION', color: 'pink' }
  };

  // Set columns from config
  useEffect(() => {
    setAvailableColumns(Object.keys(columnConfig));
  }, []);

  // Memoize fetchLineItemData to prevent unnecessary re-creation
  const fetchLineItemData = useCallback(async () => {
    if (!selectedBank || !selectedCategory || !selectedCurrentPeriod || !selectedPreviousPeriod) {
      setLineItemData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${config.apiUrl}/filtered-data`, {
        params: {
          business_segment: selectedBusinessSegment,
          bank: selectedBank,
          category: selectedCategory,
          view_type: selectedViewType
        },
        headers: getDefaultHeaders(country)
      });

      console.log('API Response:', response.data);
      console.log('Selected Current Period:', selectedCurrentPeriod);
      console.log('Selected Previous Period:', selectedPreviousPeriod);
      console.log('Data Length:', response.data.data?.length || 0);
      
      // Debug: Check what period columns are available in the first item
      if (response.data.data && response.data.data.length > 0) {
        const firstItem = response.data.data[0];
        console.log('First item keys:', Object.keys(firstItem));
        console.log('Current period value:', firstItem[selectedCurrentPeriod]);
        console.log('Previous period value:', firstItem[selectedPreviousPeriod]);
      }
      
      // Transform the data to match our table structure with hierarchical support
      const rawData = response.data.data || [];
      
      console.log('Raw API data:', rawData);
      console.log('Raw data length:', rawData.length);
      console.log('Sample raw item:', rawData[0]);
      console.log('All items:', rawData.map(item => ({ id: item.id, item: item.Item })));
      
      // Create a proper hierarchical structure based on ID patterns
      const createHierarchicalStructure = (data) => {
        console.log('Creating hierarchical structure for', data.length, 'items');
        
        // Extract base ID without suffix (e.g., "1.1_QoQ" -> "1.1")
        const getBaseId = (id) => {
          return id.toString().split('_')[0];
        };
        
        // Sort data by ID to maintain order
        const sortedData = data.sort((a, b) => {
          const aId = a.id.toString();
          const bId = b.id.toString();
          return aId.localeCompare(bId, undefined, { numeric: true });
        });
        
        console.log('Sorted data:', sortedData.map(item => ({ id: item.id, item: item.Item })));
        
        // Process each item individually to determine its hierarchy
        const processedItems = sortedData.map(item => {
          const itemId = item.id.toString();
          const baseId = getBaseId(itemId);
          
          // Check if this item has children (other items with base IDs that start with this base ID + ".")
          const hasChildren = sortedData.some(otherItem => {
            const otherBaseId = getBaseId(otherItem.id);
            return otherBaseId.startsWith(baseId + '.') && otherBaseId !== baseId;
          });
          
          // Determine level based on base ID structure
          const level = baseId.split('.').length - 1;
          
          // Find parent ID
          let parentId = null;
          if (level > 0) {
            const parentBaseId = baseId.substring(0, baseId.lastIndexOf('.'));
            const parent = sortedData.find(p => getBaseId(p.id) === parentBaseId);
            parentId = parent ? parent.id : null;
          }
          
          console.log(`Item ${itemId} (${item.Item}): baseId=${baseId}, level=${level}, hasChildren=${hasChildren}, parentId=${parentId}`);
          
          return {
            ...item,
            level: level,
            isExpandable: hasChildren,
            parentId: parentId
          };
        });
        
        console.log('Processed items:', processedItems.length);
        console.log('Hierarchy summary:', processedItems.map(item => ({ 
          id: item.id, 
          item: item.Item, 
          level: item.level, 
          isExpandable: item.isExpandable,
          parentId: item.parentId 
        })));
        
        return processedItems;
      };
      
      const hierarchicalData = createHierarchicalStructure(rawData);
      console.log('Hierarchical data created:', hierarchicalData.map(item => ({ 
        id: item.id, 
        item: item.Item, 
        level: item.level, 
        isExpandable: item.isExpandable,
        parentId: item.parentId 
      })));
      
      const transformedData = hierarchicalData.map((item, index) => {
        // Map period format - convert q1_2025 to jfm_2025, q2_2025 to amj_2025, etc.
        let currentPeriodKey = selectedCurrentPeriod;
        if (selectedCurrentPeriod.includes('q1_')) {
          currentPeriodKey = selectedCurrentPeriod.replace('q1_', 'jfm_');
        } else if (selectedCurrentPeriod.includes('q2_')) {
          currentPeriodKey = selectedCurrentPeriod.replace('q2_', 'amj_');
        } else if (selectedCurrentPeriod.includes('q3_')) {
          currentPeriodKey = selectedCurrentPeriod.replace('q3_', 'jas_');
        } else if (selectedCurrentPeriod.includes('q4_')) {
          currentPeriodKey = selectedCurrentPeriod.replace('q4_', 'ond_');
        }
        
        let previousPeriodKey = selectedPreviousPeriod;
        if (selectedPreviousPeriod.includes('q1_')) {
          previousPeriodKey = selectedPreviousPeriod.replace('q1_', 'jfm_');
        } else if (selectedPreviousPeriod.includes('q2_')) {
          previousPeriodKey = selectedPreviousPeriod.replace('q2_', 'amj_');
        } else if (selectedPreviousPeriod.includes('q3_')) {
          previousPeriodKey = selectedPreviousPeriod.replace('q3_', 'jas_');
        } else if (selectedPreviousPeriod.includes('q4_')) {
          previousPeriodKey = selectedPreviousPeriod.replace('q4_', 'ond_');
        }
        
        console.log(`Period mapping: ${selectedCurrentPeriod} -> ${currentPeriodKey}, ${selectedPreviousPeriod} -> ${previousPeriodKey}`);
        console.log(`Available keys in item:`, Object.keys(item).filter(key => 
          key.includes('jfm') || key.includes('amj') || key.includes('jas') || key.includes('ond') || 
          key.includes('h1') || key.includes('h2') || key.includes('9m') || key.includes('fy')
        ));
        
        // Access period columns using the mapped keys
        const currentValue = item[currentPeriodKey] || 0;
        const previousValue = item[previousPeriodKey] || 0;
        const varianceAmount = parseFloat(currentValue) - parseFloat(previousValue);
        const variancePercent = parseFloat(previousValue) !== 0 ? (varianceAmount / Math.abs(parseFloat(previousValue))) * 100 : 0;
        
        console.log(`Item ${item.Item}: current=${currentValue} (${currentPeriodKey}), previous=${previousValue} (${previousPeriodKey}), variance=${varianceAmount}`);
        
        // Use the hierarchical structure we created
        const itemId = item.id.toString();
        const hasChildren = item.isExpandable || false;
        const level = item.level || 0;
        const parentId = item.parentId || null;
        
        console.log(`Item ${itemId} (${item.Item}): level=${level}, hasChildren=${hasChildren}, parentId=${parentId}`);
        
        return {
          id: itemId,
          line_item: item.Item || '',
          current: currentValue,
          previous: previousValue,
          variance_amount: varianceAmount,
          variance_percent: variancePercent,
          segment: selectedBusinessSegment,
          ai_explanation: generateAIExplanation(item, currentValue, previousValue, varianceAmount, variancePercent),
          isExpandable: hasChildren,
          level: level,
          parentId: parentId
        };
      });
      
      console.log('Transformed data:', transformedData.map(item => ({ 
        id: item.id, 
        line_item: item.line_item, 
        level: item.level, 
        isExpandable: item.isExpandable,
        parentId: item.parentId 
      })));

      setLineItemData(transformedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLineItemData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessSegment, selectedBank, selectedCategory, selectedCurrentPeriod, selectedPreviousPeriod, selectedViewType, country]);

  // Use ref to track previous selections to avoid unnecessary calls
  const prevSelectionsRef = useRef('');
  
  // Fetch line item data when all required filters are selected
  useEffect(() => {
    const selectionsKey = `${selectedBank}-${selectedCategory}-${selectedCurrentPeriod}-${selectedPreviousPeriod}-${selectedViewType}`;
    
    if (selectedBank && selectedCategory && selectedCurrentPeriod && selectedPreviousPeriod) {
      // Only fetch if selections actually changed
      if (prevSelectionsRef.current !== selectionsKey) {
        prevSelectionsRef.current = selectionsKey;
        fetchLineItemData();
      }
    } else {
      setLineItemData([]);
      prevSelectionsRef.current = '';
    }
  }, [selectedBank, selectedCategory, selectedCurrentPeriod, selectedPreviousPeriod, selectedViewType, fetchLineItemData]);

  // Format values with K/M suffixes for integers, keep decimals as-is
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value.toString();
    
    // If it's a decimal value, return as-is
    if (numValue % 1 !== 0) {
      return numValue.toString();
    }
    
    // For integer values, apply K/M formatting
    if (Math.abs(numValue) >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    } else if (Math.abs(numValue) >= 1000) {
      return (numValue / 1000).toFixed(1) + 'K';
    } else {
      return numValue.toString();
    }
  };

  // Generate meaningful AI explanation based on financial data
  const generateAIExplanation = (item, currentValue, previousValue, varianceAmount, variancePercent) => {
    const itemName = item.Item;
    const currentPeriod = selectedCurrentPeriod;
    const previousPeriod = selectedPreviousPeriod;
    const viewType = selectedViewType;
    
    // Parse numeric values
    const current = parseFloat(currentValue) || 0;
    const previous = parseFloat(previousValue) || 0;
    const variance = parseFloat(varianceAmount) || 0;
    const variancePct = parseFloat(variancePercent) || 0;
    
    // Determine trend direction
    const isPositive = variance > 0;
    const isNegative = variance < 0;
    const isNeutral = variance === 0;
    
    // Determine magnitude of change
    const isSignificant = Math.abs(variancePct) > 10; // More than 10% change
    const isModerate = Math.abs(variancePct) > 5 && Math.abs(variancePct) <= 10; // 5-10% change
    const isMinor = Math.abs(variancePct) > 0 && Math.abs(variancePct) <= 5; // 0-5% change
    
    // Generate explanation based on the data
    let explanation = '';
    
    if (isNeutral) {
      explanation = `${itemName} remained stable at ${formatValue(current)} between ${previousPeriod} and ${currentPeriod}. No significant changes observed.`;
    } else if (isPositive) {
      if (isSignificant) {
        explanation = `${itemName} showed strong growth of ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}. This represents a significant positive trend.`;
      } else if (isModerate) {
        explanation = `${itemName} increased by ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}. Moderate positive growth observed.`;
      } else {
        explanation = `${itemName} saw a slight increase of ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}.`;
      }
    } else if (isNegative) {
      if (isSignificant) {
        explanation = `${itemName} declined significantly by ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}. This represents a concerning negative trend.`;
      } else if (isModerate) {
        explanation = `${itemName} decreased by ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}. Moderate decline observed.`;
      } else {
        explanation = `${itemName} saw a slight decrease of ${formatValue(Math.abs(variance))} (${Math.abs(variancePct).toFixed(1)}%) from ${previousPeriod} to ${currentPeriod}.`;
      }
    }
    
    return explanation;
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setExpandedRows(new Set());
    setCurrentPage(1);
    setShowAll(false);
  };

  // Get active filters for display
  const activeFilters = [
    { label: selectedBank, color: 'blue' },
    { label: selectedCategory, color: 'green' },
    { label: selectedCurrentPeriod, color: 'purple' },
    { label: selectedPreviousPeriod, color: 'orange' }
  ].filter(filter => filter.label);

  // Calculate visible data based on pagination and expansion
  const totalItems = lineItemData.length;
  const startIndex = showAll ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = showAll ? totalItems : Math.min(startIndex + itemsPerPage, totalItems);
  const visibleData = lineItemData.slice(startIndex, endIndex);

  if (!selectedBusinessSegment) {
    return (
      <div className="line-item-analysis">
        <div className="line-item-analysis-header">
          <div className="header-icon">
            <Table className="w-4 h-4" />
          </div>
          <div className="header-content">
            <h3 className="header-title">Line Item Analysis</h3>
            <p className="header-subtitle">Hierarchical view of financial items with detailed breakdowns</p>
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No business segment selected</h3>
          <p>Please select a business segment to view line item analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div className="line-item-analysis">
      {/* Data Summary */}
      <div className="data-summary">
        <span>Showing data for: {selectedBank || 'All Banks'} | {selectedCurrentPeriod} vs {selectedPreviousPeriod}</span>
        <div className="summary-stats">
          <span className="stat-item">
            <span className="stat-dot purple"></span>
            {totalItems} items
          </span>
          <span className="stat-item">
            <span className="stat-dot blue"></span>
            {selectedColumns.length} columns
          </span>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spinner" size={24} />
            <span>Loading data...</span>
          </div>
        ) : error ? (
          <div className="error-state">
            <span>Error: {error}</span>
          </div>
        ) : visibleData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🕐</div>
            <h3>No data available</h3>
            <p>Please adjust your filters to view line item data</p>
          </div>
        ) : (
          <div className="data-table">
            <div className="table-container">
              <div className="table-header">
                {selectedColumns.map(columnKey => {
                  const config = columnConfig[columnKey];
                  return (
                    <div key={columnKey} className="table-header-cell">
                      <span className={`header-dot ${config.color}`}></span>
                      {config.label}
                    </div>
                  );
                })}
              </div>
              
              <div className="table-body">
              {visibleData.map((row) => (
                <div key={row.id} className={`table-row level-${row.level}`}>
                  {selectedColumns.map(columnKey => {
                    const config = columnConfig[columnKey];
                    let cellContent = '';
                    
                    if (columnKey === 'line_item') {
                      cellContent = (
                        <div className="line-item-cell">
                          {row.isExpandable && (
                            <button 
                              className="expand-button"
                              onClick={() => toggleRowExpansion(row.id)}
                            >
                              {expandedRows.has(row.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          )}
                          <span className={`line-item-dot ${config.color}`}></span>
                          <span className={`line-item-text level-${row.level}`}>
                            {row.line_item}
                          </span>
                        </div>
                      );
                    } else if (columnKey === 'current' || columnKey === 'previous') {
                      cellContent = (
                        <div className="value-cell">
                          <span className={`value-dot ${config.color}`}></span>
                          {formatValue(row[columnKey])}
                        </div>
                      );
                    } else if (columnKey === 'variance_amount') {
                      const isPositive = row.variance_amount > 0;
                      cellContent = (
                        <div className="variance-cell">
                          <span className={`variance-dot ${isPositive ? 'green' : 'red'}`}></span>
                          {row.variance_amount >= 0 ? '+' : ''}{formatValue(row.variance_amount)}
                          {isPositive && <span className="trend-arrow">↗</span>}
                        </div>
                      );
                    } else if (columnKey === 'variance_percent') {
                      const isPositive = row.variance_percent > 0;
                      cellContent = (
                        <div className={`percentage-cell ${isPositive ? 'positive' : 'negative'}`}>
                          <span className={`percentage-dot ${isPositive ? 'green' : 'red'}`}></span>
                          {row.variance_percent >= 0 ? '+' : ''}{row.variance_percent.toFixed(1)}%
                        </div>
                      );
                    } else if (columnKey === 'segment') {
                      cellContent = (
                        <div className="segment-cell">
                          <span className={`segment-dot ${config.color}`}></span>
                          {row.segment}
                        </div>
                      );
                    } else if (columnKey === 'ai_explanation') {
                      // Truncate AI explanation to 2-3 words
                      const words = row.ai_explanation.split(' ');
                      const truncatedText = words.slice(0, 3).join(' ') + (words.length > 3 ? '...' : '');
                      
                      const handleMouseEnter = (e) => {
                        const tooltip = e.currentTarget.querySelector('.ai-explanation-tooltip');
                        if (tooltip) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          tooltip.style.left = `${rect.left + rect.width / 2}px`;
                          tooltip.style.top = `${rect.top - 10}px`;
                          tooltip.style.transform = 'translateX(-50%)';
                        }
                      };
                      
                      cellContent = (
                        <div 
                          className="ai-explanation"
                          onMouseEnter={handleMouseEnter}
                        >
                          {truncatedText}
                          <div className="ai-explanation-tooltip">
                            {row.ai_explanation}
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div key={columnKey} className="table-cell">
                        {cellContent}
                      </div>
                    );
                  })}
                </div>
              ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!showAll && totalItems > itemsPerPage && (
        <div className="pagination">
          <span className="pagination-info">
            <span className="pagination-dot purple"></span>
            Showing {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
          </span>
          <button 
            className="show-all-button"
            onClick={() => setShowAll(true)}
          >
            Show All ({totalItems})
          </button>
        </div>
      )}

      {showAll && totalItems > itemsPerPage && (
        <div className="pagination">
          <span className="pagination-info">
            <span className="pagination-dot purple"></span>
            Showing {totalItems} of {totalItems} items
          </span>
          <button 
            className="show-less-button"
            onClick={() => setShowAll(false)}
          >
            Show Less
          </button>
        </div>
      )}
    </div>
  );
};

export default ModernLineItemAnalysis;